import { ArrowLeft, UserCog, AlertCircle } from 'lucide-react'
import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { PosUserForm } from '@/components/features/pos-users/PosUserForm'
import { PosUserTypePermissions } from '@/components/features/pos-users/PosUserTypePermissions'
import { LoadingSpinner } from '@/components/ui/LoadingSpinner'
import { useWriteAccess } from '@/hooks/use-permissions'
import { usePosUser, useUpdatePosUser } from '@/hooks/use-pos-users'
import { type PosUserFormData } from '@/lib/validation/pos-user-schemas'
import { type UpdatePosUserRequest } from '@/models/domain/pos-user'

function PosEditUserDescription() {
  return (
    <div className="mb-6 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4 text-sm text-gray-700 dark:text-gray-300 space-y-2">
      <p>
        Modify fields in the tabs below. Click Update POS User to store this POS User&apos;s
        details.
      </p>
      <p>
        If you change the POS User Type, you may need to populate different fields. A District
        Manager will have a District, while Store Managers and below have Locations.
      </p>
      <p>
        Districts and Locations must exist before assigning POS Users. Before reassigning a POS
        User&apos;s User Type, you must create at least one District and one Location.
      </p>
      <PosUserTypePermissions />
    </div>
  )
}

function buildUpdateRequest(data: PosUserFormData): UpdatePosUserRequest {
  const request: UpdatePosUserRequest = {
    first_name: data.first_name,
    last_name: data.last_name,
    level: data.level,
    active: data.active,
    expires: data.expires ? new Date(data.expires).toISOString() : null,
    expires_eos: data.expires_eos,
  }

  if (data.email) request.email = data.email
  if (data.pin) request.pin = data.pin
  if (data.location_id) request.location_id = data.location_id
  if (data.district_id) request.district_id = data.district_id

  return request
}

function EditPosUserLoadingView() {
  return (
    <div className="flex items-center justify-center min-h-screen">
      <div className="flex flex-col items-center gap-3">
        <LoadingSpinner size="lg" />
        <p className="text-sm text-gray-600 dark:text-gray-400">Loading POS user data...</p>
      </div>
    </div>
  )
}

function EditPosUserErrorView({
  fetchError,
  onBack,
}: {
  fetchError: Error | null
  onBack: () => void
}) {
  return (
    <div className="p-6 max-w-5xl mx-auto">
      <button
        onClick={onBack}
        className="inline-flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 transition-colors mb-4"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to POS users
      </button>

      <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-6">
        <div className="flex items-start gap-3">
          <AlertCircle className="w-6 h-6 text-red-600 dark:text-red-400 flex-shrink-0" />
          <div>
            <h3 className="text-lg font-medium text-red-800 dark:text-red-200">
              Error loading POS user
            </h3>
            <p className="mt-1 text-sm text-red-700 dark:text-red-300">
              {fetchError
                ? 'Failed to load POS user data. Please try again.'
                : 'POS user not found.'}
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}

export function EditPosUserPage() {
  const { id = '' } = useParams<{ id: string }>()
  const numericId = Number(id)
  const navigate = useNavigate()
  const [error, setError] = useState<string | null>(null)

  const { data: user, isLoading, error: fetchError } = usePosUser(numericId)
  const updateMutation = useUpdatePosUser()
  const canWrite = useWriteAccess('cs_pos_users_w')

  const handleSubmit = async (data: PosUserFormData) => {
    setError(null)

    try {
      await updateMutation.mutateAsync({ id: numericId, request: buildUpdateRequest(data) })
      navigate('/admin/pos-users', {
        state: { success: `POS user ${data.first_name} ${data.last_name} updated successfully!` },
      })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update POS user')
    }
  }

  const goBack = () => {
    navigate('/admin/pos-users')
  }

  if (isLoading) {
    return <EditPosUserLoadingView />
  }

  if (fetchError || !user) {
    return <EditPosUserErrorView fetchError={fetchError} onBack={goBack} />
  }

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <div className="mb-6">
        <button
          onClick={goBack}
          className="inline-flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 transition-colors mb-4"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to POS users
        </button>

        <div className="flex items-center gap-3">
          <div className="p-2 bg-primary-100 dark:bg-primary-900/20 rounded-lg">
            <UserCog className="w-6 h-6 text-primary-600 dark:text-primary-400" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Edit POS User</h1>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Update settings for {user.first_name} {user.last_name} (#{user.id})
            </p>
          </div>
        </div>
      </div>

      <PosEditUserDescription />

      <PosUserForm
        mode="edit"
        user={user}
        onSubmit={handleSubmit}
        isLoading={updateMutation.isPending}
        error={error}
        canWrite={canWrite}
      />
    </div>
  )
}
